/**
 * Dynamic Trade Condition Registry
 *
 * Frontend mirror of the backend dynamic trade condition registry.
 * Defines all available dynamic conditions and their parameter schemas.
 *
 * Backend reference: rust-backend/crates/backtest/src/tradecondition/registry.rs
 */

import { ParamSchema } from "./types";

// Extended param schema for dynamic conditions (supports arrays)
export interface DynamicParamSchema extends ParamSchema {
  isArray?: boolean;  // If true, value is array, UI shows comma-separated input
}

// Dynamic condition schema (similar to SignalSchema but for trade conditions)
export interface DynamicConditionSchema {
  key: string;                              // Unique identifier (e.g., "delta_hedge_quantity")
  label: string;                            // Display name
  description: string;
  params: Record<string, DynamicParamSchema>;
  // Custom validator - returns true if valid, or error message string
  // Can optionally receive currentLegIndex for validation that depends on which leg this is applied to
  validate?: (params: Record<string, unknown>, currentLegIndex?: number) => true | string;
}

// A configured dynamic condition instance
export interface ConfiguredDynamicCondition {
  id: string;                               // Unique instance ID
  type: string;                             // Condition type key (e.g., "delta_hedge_quantity")
  params: Record<string, unknown>;          // Filled parameter values
}

/**
 * Dynamic Trade Condition Registry
 *
 * Each condition definition includes:
 * - key: Unique identifier matching backend
 * - label: Human-readable name
 * - description: Explains what the condition does
 * - params: Parameter definitions with types, labels, and validation
 */
export const DYNAMIC_CONDITION_REGISTRY: Record<string, DynamicConditionSchema> = {
  delta_hedge_quantity: {
    key: "delta_hedge_quantity",
    label: "Delta Hedge Quantity",
    description: "Calculate delta hedge quantity and direction based on position data",
    params: {
      leg_idx: {
        type: "integer",
        label: "Leg Numbers",
        required: true,
        min: 1,
        placeholder: "1, 2, 3",
        description: "Which legs to hedge against (1 = first leg, comma-separated)",
        isArray: true,
      },
    },
  },
  strike_relative_to_spot: {
    key: "strike_relative_to_spot",
    label: "Strike Relative to Spot",
    description: "Calculate strike price relative to spot price with absolute and/or relative adjustments",
    params: {
      symbol: {
        type: "string",
        label: "Symbol",
        required: false,
        placeholder: "SPY (leave empty to use option's symbol)",
        description: "The underlying symbol to get spot price for. Leave empty to use the option's symbol.",
      },
      absolute: {
        type: "number",
        label: "Absolute Adjustment",
        required: false,
        placeholder: "5 or -5",
        description: "Absolute value to add/subtract from spot (e.g., 5 or -5)",
      },
      relative: {
        type: "number",
        label: "Relative Adjustment (%)",
        required: false,
        placeholder: "5 or -5",
        description: "Percentage to add/subtract from spot (e.g., 5 for +5%, -5 for -5%)",
      },
    },
    validate: (params: Record<string, unknown>) => {
      // At least one of absolute or relative must be provided
      if (params.absolute === undefined && params.relative === undefined) {
        return "At least one of 'Absolute Adjustment' or 'Relative Adjustment' must be provided";
      }

      // Both cannot be provided together
      if (params.absolute !== undefined && params.relative !== undefined) {
        return "Cannot provide both 'Absolute Adjustment' and 'Relative Adjustment' together. Choose one.";
      }

      return true;
    },
  },
  strike_relative_to_leg: {
    key: "strike_relative_to_leg",
    label: "Strike Relative to Leg",
    description: "Calculate strike price relative to another leg's strike price",
    params: {
      leg_idx: {
        type: "integer",
        label: "Leg Number",
        required: true,
        min: 1,
        placeholder: "e.g., 2 (must be different from current leg)",
        description: "The leg number to get the strike price from (1 = first leg, must be different from current leg)",
      },
      absolute: {
        type: "number",
        label: "Absolute Adjustment",
        required: false,
        placeholder: "5 or -5",
        description: "Absolute value to add/subtract from the other leg's strike (e.g., 5 or -5)",
      },
      relative: {
        type: "number",
        label: "Relative Adjustment (%)",
        required: false,
        placeholder: "5 or -5",
        description: "Percentage to add/subtract from the other leg's strike (e.g., 5 for +5%, -5 for -5%)",
      },
    },
    validate: (params: Record<string, unknown>, currentLegIndex?: number) => {
      // At least one of absolute or relative must be provided
      if (params.absolute === undefined && params.relative === undefined) {
        return "At least one of 'Absolute Adjustment' or 'Relative Adjustment' must be provided";
      }

      // Check if leg_idx is the same as current leg
      if (currentLegIndex !== undefined && params.leg_idx !== undefined) {
        const selectedLegIdx = typeof params.leg_idx === 'number' ? params.leg_idx : parseInt(String(params.leg_idx), 10);
        if (!isNaN(selectedLegIdx) && selectedLegIdx === currentLegIndex + 1) {
          return `Cannot reference the same leg (Leg ${currentLegIndex + 1}). Please select a different leg number.`;
        }
      }

      return true;
    },
  },
};

// Helper to get all dynamic conditions as array for dropdowns
export const getDynamicConditionOptions = (): { value: string; label: string }[] => {
  return Object.values(DYNAMIC_CONDITION_REGISTRY).map((schema) => ({
    value: schema.key,
    label: schema.label,
  }));
};

// Helper to get a specific dynamic condition schema
export const getDynamicConditionSchema = (key: string): DynamicConditionSchema | undefined => {
  return DYNAMIC_CONDITION_REGISTRY[key];
};

/**
 * Check if a dynamic condition has all required params filled
 */
export const isDynamicConditionComplete = (
  condition: ConfiguredDynamicCondition,
  currentLegIndex?: number
): boolean => {
  if (!condition.type) return false;

  const schema = DYNAMIC_CONDITION_REGISTRY[condition.type];
  if (!schema) return false;

  // Check all required params are present and valid
  for (const [key, paramSchema] of Object.entries(schema.params)) {
    if (paramSchema.required) {
      const value = condition.params[key];
      if (value === undefined || value === null || value === "") {
        return false;
      }
      // For arrays, check if non-empty
      if (paramSchema.isArray && Array.isArray(value) && value.length === 0) {
        return false;
      }
    }
  }

  // Run custom validator if exists
  if (schema.validate) {
    const result = schema.validate(condition.params, currentLegIndex);
    if (result !== true) {
      return false;
    }
  }

  return true;
};

/**
 * Get a human-readable summary of the configured dynamic condition
 */
export const getDynamicConditionSummary = (condition: ConfiguredDynamicCondition): string | null => {
  if (!condition.type) return null;

  const schema = DYNAMIC_CONDITION_REGISTRY[condition.type];
  if (!schema) return null;

  const parts: string[] = [];
  for (const [key, value] of Object.entries(condition.params)) {
    if (value !== undefined && value !== null && value !== "") {
      const paramSchema = schema.params[key];
      if (paramSchema) {
        if (paramSchema.isArray && Array.isArray(value)) {
          parts.push(`${paramSchema.label}: [${value.join(", ")}]`);
        } else {
          parts.push(`${paramSchema.label}: ${value}`);
        }
      }
    }
  }

  return parts.length > 0 ? parts.join(", ") : null;
};
